import type { Identifier, Observation } from "@medplum/fhirtypes";
import { WEARABLE_IDENTIFIER_SYSTEM_PROVIDER } from "../wearables/types";

/** Canonical identifier for synthetic / demo Observations in Trends. */
export const OBSERVATION_SOURCE_IDENTIFIER_SYSTEM = "vibrant.observation.source" as const;

/** Legacy demo marker written before the canonical system existed. */
export const LEGACY_OBSERVATION_SOURCE_IDENTIFIER_SYSTEM =
  "http://vibrant.health/observation-source" as const;

/** Wearable provider slug used by synthetic demo trend Observations. */
export const SYNTHETIC_TRENDS_PROVIDER_SLUG = "seed" as const;

export const OBSERVATION_SOURCE_VALUES = {
  SyntheticTrendsDemo: "synthetic-trends-demo",
  SandboxSeed: "sandbox-seed",
} as const;

export type ObservationSourceValue =
  (typeof OBSERVATION_SOURCE_VALUES)[keyof typeof OBSERVATION_SOURCE_VALUES];

export const SYNTHETIC_TREND_ORIGIN_LABEL = "Demo data" as const;

function getIdentifierValue(observation: Observation, system: string): string | undefined {
  return observation.identifier?.find((identifier) => identifier.system === system)?.value;
}

/**
 * Returns the observation-source identifier value when present (canonical or legacy).
 */
export function getObservationSourceValue(observation: Observation): string | undefined {
  return (
    getIdentifierValue(observation, OBSERVATION_SOURCE_IDENTIFIER_SYSTEM) ??
    getIdentifierValue(observation, LEGACY_OBSERVATION_SOURCE_IDENTIFIER_SYSTEM)
  );
}

/**
 * Builds the canonical synthetic observation-source identifier for writes.
 */
export function toObservationSourceIdentifier(value: ObservationSourceValue): Identifier {
  return {
    system: OBSERVATION_SOURCE_IDENTIFIER_SYSTEM,
    value,
  };
}

/**
 * Returns true when the Observation is tagged as synthetic/demo data.
 */
export function isSyntheticObservation(
  observation: Observation,
  options?: { hasSandboxSeedIdentifier?: boolean },
): boolean {
  if (getObservationSourceValue(observation)) {
    return true;
  }

  if (options?.hasSandboxSeedIdentifier) {
    return true;
  }

  return (
    getIdentifierValue(observation, WEARABLE_IDENTIFIER_SYSTEM_PROVIDER) ===
    SYNTHETIC_TRENDS_PROVIDER_SLUG
  );
}
